import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {OrderItem} from '../../_models/order-item';
import {OrganizationService} from '../../_services/organization.service';
import {UserService} from '../../_services/user.service';
import {BaseManagerComponent} from '../base-manager.component';
import {AuthenticationService} from '../../_services/authentication.service';
import {PickupLocationService} from '../../_services/pickup-location.service';
import {OrderItemSummary} from '../../_models/order-item-summary';
import {addCurrency, multiplyCurrency, subtractCurrency} from '../../_utils/currency-math';
import {FeatureKeys} from '../../_constants/feature-keys';
import {CompDefinition} from '../../_models/comp-definition';
import {CompDefinitionTypes} from '../../_constants/comp-definition-types';
import {OrderItemCredit} from '../../_models/order-item-credit';
import {OrderStatuses} from '../../_constants/order-statuses';
import {OrganizationTerminalService} from '../../_services/organization-terminal.service';
import {MembershipService} from '../../_services/membership.service';
import {MembershipProgramFeeStructure} from '../../_constants/membership-program-fee-structure';
import {MembershipTokenGroup} from '../../_models/membership-token-group';
import {OrderItemSelection} from '../../_models/order-item-selection';

@Component({
    selector: 'bb-display-order-item',
    templateUrl: './display-order-item.component.html',
    styleUrls: ['./display-order-item.component.scss']
})
export class DisplayOrderItemComponent extends BaseManagerComponent implements OnInit, OnChanges {
    @Input() item: OrderItem;
    @Input() itemSummary: OrderItemSummary;
    @Input() additionalPrice: number;
    @Input() level = 1;
    @Input() hideHeader = false;
    @Input() hideImages = false;
    @Input() removePadding = false;
    @Input() count: number;
    @Input() compact = false;
    @Input() details = false;
    @Input() largeFont = false;
    @Input() selectedItemIds: number[];
    @Input() editable = false;
    @Input() orderStatus: string;
    @Input() availableOrderComps: CompDefinition[] = [];
    @Input() showImageOverride = false;
    @Input() hideTotal = false;

    @Output() itemsSelected = new EventEmitter<OrderItem[]>();
    @Output() updateItem: EventEmitter<OrderItem> = new EventEmitter<OrderItem>();
    @Output() add: EventEmitter<OrderItemSummary> = new EventEmitter<OrderItemSummary>();
    @Output() remove: EventEmitter<OrderItemSummary> = new EventEmitter<OrderItemSummary>();
    @Output() imageLoaded = new EventEmitter<any>();
    @Output() addCredit = new EventEmitter<OrderItem>();
    @Output() removeCredit = new EventEmitter<any>();
    @Output() removeTokens = new EventEmitter<OrderItem>();
    @Output() addTokens = new EventEmitter<OrderItem>();

    tokenGroup: MembershipTokenGroup;
    miscItem = false;
    filteredComps: CompDefinition[] = [];
    isTerminal = false;
    displaySelections: OrderItemSelection[];

    compDefinitionTypes = CompDefinitionTypes;
    featureKeys = FeatureKeys;
    orderStatuses = OrderStatuses;
    multiplyCurrency = multiplyCurrency;
    addCurrency = addCurrency;

    constructor(
        private organizationTerminalService: OrganizationTerminalService,
        authenticationService: AuthenticationService,
        organizationService: OrganizationService,
        pickupLocationService: PickupLocationService,
        userService: UserService,
        membershipService: MembershipService
    ) {
        super(authenticationService,
            organizationService,
            pickupLocationService,
            userService,
            membershipService);
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.isTerminal = this.organizationTerminalService.isTerminal;
        this.init();
    }

    init() {
        if (!!this.itemSummary) {
            this.item = new OrderItem(this.itemSummary.items[0]);
        } else {
            this.item = new OrderItem(this.item);
        }
        this.miscItem = !this.item.menuItem;
        if (!!this.availableOrderComps) {
            this.filteredComps = this.availableOrderComps
                .filter(c => !c.items
                    || c.items.length === 0
                    || c.items.some(p => !!this.item.selectedPrice && p.priceId === this.item.selectedPrice.id));
        }

        if (!!this.organization && !!this.organization.tokenGroups && !!this.item && !!this.item.selectedPrice) {
            this.tokenGroup = this.organization.tokenGroups.find(t => !!t.items
                && t.items.some(i => i.priceId === this.item.selectedPrice.id));
        }

        this.displaySelections = !!this.item.selections && this.item.selections.length > 0 ?
            this.item.selections.filter(selection => {
                if (!!selection.menuItemSelection.menuSelection.defaultOptions
                    && selection.menuItemSelection.menuSelection.defaultOptions.length > 0
                    && (!selection.selectedItems || selection.selectedItems.length === 0)) {
                    return true;
                }

                if (selection.countTotal() > 0) {
                    return true;
                }

                if (!!selection.menuItemSelection.menuSelection.defaultOptions) {
                    for (const defaultItem of selection.menuItemSelection.menuSelection.defaultOptions) {
                        if (!defaultItem.printForDefault) {
                            if (!selection.selectedItems.some(i => i.menuSelectionItem.id === defaultItem.id)) {
                                return true;
                            }
                        }
                    }
                }
                for (const selectedItem of selection.selectedItems) {
                    if (!selectedItem.menuSelectionItem.defaultSelection || selectedItem.menuSelectionItem.printForDefault || selectedItem.orderItem.modifiers.length > 0) {
                        return true;
                    }
                }

                return false;
            }) : [];

        this.displaySelections.forEach(selection => {
            if (!!selection.menuItemSelection.menuSelection.defaultOptions) {
                selection.menuItemSelection.menuSelection.defaultOptions
                    .forEach(d => d.removed = !selection.selectedItems.some(i => i.menuSelectionItem.id === d.id));
            }
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!!changes.itemSummary && !changes.itemSummary.firstChange) {
            this.init();
        }
    }

    childRow(additionalLevel = 0) {
        return 'border-left: 1px solid #fff; margin-left: ' + ((this.level + additionalLevel + 1) * 15)
            + 'px; max-width: calc((calc(9 / var(--ion-grid-columns, 12)) * 100%) - ' + ((this.level + 1) * 15) + ')';
    }

    parentRow() {
        return 'margin-left: ' + (this.level * 15) + 'px;';
    }

    adjustedTokenPrice(price) {
        return !!this.count ? (price * this.count) : price;
    }

    adjustedPrice(price) {
        if (!!price) {
            return !!this.count ? multiplyCurrency(price, this.count) : price;
        } else {
            return null;
        }
    }

    async addCreditClicked(event) {
        event.stopPropagation();
        this.addCredit.emit(this.item);
    }

    async removeCreditClicked(event, credit: OrderItemCredit) {
        event.stopPropagation();
        this.removeCredit.emit({orderItem: this.item, credit});
    }

    removeMisc(event) {
        event.stopPropagation();
        this.remove.emit(this.itemSummary);
    }

    addClicked() {
        if (!!this.item.membershipRenewal) {
            this.item.membershipRenewal.quantity++;
            this.calculateMembershipFeeInformation();
            this.updateItem.emit(this.item);
        } else {
            this.add.emit(this.itemSummary);
        }
    }

    removeClicked() {
        if (!!this.item.membershipRenewal && this.item.membershipRenewal.quantity > 1) {
            this.item.membershipRenewal.quantity--;
            this.calculateMembershipFeeInformation();
            this.updateItem.emit(this.item);
        } else {
            this.remove.emit(this.itemSummary);
        }
    }

    calculateMembershipFeeInformation() {
        if (!this.organization.organizationPreference.monthlyMembershipSubscription) {
            this.item.membershipHostFee = multiplyCurrency(this.item.membershipRenewal.quantity, this.item.membershipRenewal.months);
            if (this.item.membershipRenewal.membershipProgram.feeStructure === MembershipProgramFeeStructure.DUAL) {
                this.item.membershipOrganizationFee = this.item.membershipHostFee;
            }
            const amount = this.item.membershipFeeIncluded
                ? subtractCurrency(this.item.membershipRenewal.membershipProgram.amount, this.item.membershipRenewal.months)
                : this.item.membershipRenewal.membershipProgram.amount;
            this.item.total = (multiplyCurrency(this.item.membershipRenewal.quantity, amount));
        } else {
            this.item.membershipHostFee = 0;
            this.item.membershipOrganizationFee = 0;
        }
    }

    useTokens(event) {
        event.stopPropagation();
        if (this.orderStatus !== OrderStatuses.CANCELED && !!this.tokenGroup && !this.item.tokenPayment) {
            const item = new OrderItem(this.item);
            item.tokenPaymentRefunded = null;
            item.tokenPayment = this.tokenGroup.tokenCount;
            item.membershipTokenGroupId = this.tokenGroup.id;
            item.preventAutoCredit = true;
            this.addTokens.emit(item);
        }
    }

    getNameOverride() {
        if (!!this.item.giftCard) {
            return 'Gift Card';
        } else if (!!this.item.membershipRenewal) {
            return !this.item.membershipRenewal.membershipProgram
                ? this.organization.membershipLabel : this.item.membershipRenewal.membershipProgram.name;
        } else {
            return 'Misc';
        }
    }
}
